import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@one-thd/sui-atomic-components';
import {
  ReturnMethodType,
  SHIPMENT_TO_RETURN_METHOD_MAP,
} from '@src/constants/general';
import { dateFormat } from '@src/utils/general';
import { useShipmentSeparation } from '@src/hooks/useShipmentSeparation';
import { SummaryShipment } from '@src/models/returns';
import ViewUpsBarcodeDrawer from '../view-ups-barcode/ViewUpsBarcodeDrawer';
import { useReturnStore } from '../../stores/returnStore';
import { TagOnReturnSummaryPage } from '../../utils/analytics-tagging';
import { useSummaryStore } from '../../stores/summaryStore';
import { RefundTotal } from '../shared/RefundTotal';
import SummaryReturnCard from './SummaryReturnCard';
import ReturnInStoreInstructionsDrawer from './ReturnInstoreInstructionsDrawer';
import SORISInstructionsDrawer from './SORISInstructionsDrawer';
import ReturnSummaryAlert from './ReturnSummaryAlert';
import LorpInstructionsDrawer from '../lorp/LorpInstructionsDrawer';
import { LORPGroupedSummaryReturnCard } from './LORPGroupedSummaryReturnCard';

export const ReturnSummary = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [areLORPShipmentsSelected, setAreLORPShipmentsSelected] =
    useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<number>();
  const navigate = useNavigate();
  const summaryShipments = useSummaryStore((state) => state.summaryShipments);
  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const refundValues = useSummaryStore((state) => state.refundValues);

  const { lorpShipments, sortedNonLorpReturnShipments } =
    useShipmentSeparation(summaryShipments);

  const selectedShipment = summaryShipments?.find(
    (shipment) => shipment.shipmentId === selectedShipmentId,
  );

  // assuming there is only one instore shipment (PHASE_0_SORIS)
  const instoreShipment = summaryShipments?.filter(
    (shipment) => shipment.shipmentType === 'PHASE_0_SORIS',
  )?.[0];

  // assuming there is only one SORIS shipment
  const sorisShipment = summaryShipments?.filter(
    (shipment) => shipment.shipmentType === 'SORIS',
  )?.[0];

  const returnToInitialPage = () =>
    isMilitaryExchangeGuest
      ? navigate(`/myaccount/guest-order-tracking`)
      : navigate(`/myaccount/purchase-history`);

  useEffect(() => {
    if (orderDetailsV2) {
      TagOnReturnSummaryPage();
    }
  }, [orderDetailsV2]);

  const handleReturnInstructionsClick = (shipment: SummaryShipment) => {
    setSelectedShipmentId(shipment.shipmentId);
    setAreLORPShipmentsSelected(false);
    setIsDrawerOpen(true);
  };

  const handleGroupedLORPReturnInstructionsClick = () => {
    setAreLORPShipmentsSelected(true);
    setSelectedShipmentId(undefined);
    setIsDrawerOpen(true);
  };

  const getHeader = (
    method: ReturnMethodType,
    shipmentReturnByDate: string,
  ) => {
    switch (method) {
      case 'UPS':
        return (
          'Return to UPS by ' + dateFormat(shipmentReturnByDate, 'EEE, MMMM d')
        );
      case 'In-Store':
      case 'SORIS':
        return (
          'Return In-Store by ' +
          dateFormat(shipmentReturnByDate, 'EEE, MMMM d')
        );
      case 'LORP':
        return 'Return Pickup Requested';
      default:
        return '';
    }
  };

  return (
    <div>
      {/* // TODO: set a standard width for all pages? */}
      <div className="sui-max-w-[555px]">
        <ReturnSummaryAlert />
        {sortedNonLorpReturnShipments?.map((shipment, index) => {
          const returnMethod =
            SHIPMENT_TO_RETURN_METHOD_MAP[shipment.shipmentType];
          const shipmentReturnByDate = shipment.returnByDate || '';
          const header = getHeader(returnMethod, shipmentReturnByDate);
          const contactDetails = shipment?.lorpContactDetails;

          return (
            <SummaryReturnCard
              key={`summary-return-card-${index}`}
              method={returnMethod}
              header={header}
              items={shipment.itemsToReturn}
              handleReturnInstructionsClick={() =>
                handleReturnInstructionsClick(shipment)
              }
              contactDetails={contactDetails}
            />
          );
        })}
        {lorpShipments && lorpShipments.length > 0 && (
          <LORPGroupedSummaryReturnCard
            shipments={lorpShipments}
            handleReturnInstructionsClick={
              handleGroupedLORPReturnInstructionsClick
            }
          />
        )}
        <RefundTotal sessionValues={refundValues} />
        <div className="sui-flex sui-flex-col sui-w-full sm:sui-w-auto sm:sui-flex-row-reverse sui-my-12 sm:sui-my-[60px]">
          <Button variant="primary" onClick={returnToInitialPage}>
            {isMilitaryExchangeGuest
              ? 'Track Another Order'
              : 'Back to Purchase History'}
          </Button>
        </div>
      </div>

      {selectedShipment?.shipmentType === 'PHASE_0_SORIS' &&
        instoreShipment && (
          <ReturnInStoreInstructionsDrawer
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            shipment={instoreShipment}
          />
        )}

      {selectedShipment?.shipmentType === 'SORIS' && sorisShipment && (
        <SORISInstructionsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          shipment={sorisShipment}
        />
      )}

      {selectedShipment?.shipmentType === 'DROPOFF' && (
        <ViewUpsBarcodeDrawer
          displayDrawer={isDrawerOpen}
          closeDrawer={() => setIsDrawerOpen(false)}
          shipment={selectedShipment}
        />
      )}

      {(selectedShipment?.shipmentType === 'PICKUP' ||
        areLORPShipmentsSelected) && (
        <LorpInstructionsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          lorpShipments={lorpShipments}
        />
      )}
    </div>
  );
};
